import React, { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { searchGuests } from '../api';
import type { Guest, SearchResult } from '../types';

interface SearchGuestProps {
  onGuestSelect: (guest: Guest | null) => void;
  guests?: Guest[];
  isLoading?: boolean;
  eventId?: string;
}

export default function SearchGuest({ onGuestSelect, guests, isLoading: propIsLoading, eventId }: SearchGuestProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchTimeoutRef = useRef<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const performLocalSearch = (searchQuery: string, guestList: Guest[]): SearchResult[] => {
    if (!searchQuery.trim() || searchQuery.length < 1) {
      return [];
    }

    const loweredQuery = searchQuery.toLowerCase();

    return guestList
      .filter((guest) => guest.name.toLowerCase().includes(loweredQuery))
      .slice(0, 5)
      .map((guest) => ({
        guest,
        highlight: searchQuery,
        matchType: 'name' as const,
      }));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (selectedGuest || query.trim().length < 1) {
      setResults([]);
      setShowResults(false);
      return;
    }

    if (guests && guests.length > 0) {
      const localResults = performLocalSearch(query, guests);
      setResults(localResults);
      setShowResults(localResults.length > 0);
      return;
    }

    if (query.trim().length >= 2 && eventId) {
      setIsLoading(true);
      searchTimeoutRef.current = window.setTimeout(async () => {
        try {
          const response = await searchGuests(query, eventId);
          if (response.success && response.results) {
            setResults(response.results);
            setShowResults(true);
          } else {
            setResults([]);
          }
        } catch (error) {
          console.error('Failed to search guests:', error);
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [eventId, guests, query, selectedGuest]);

  const handleGuestSelect = (guest: Guest) => {
    setSelectedGuest(guest);
    setQuery(guest.name);
    onGuestSelect(guest);
    setResults([]);
    setShowResults(false);
  };

  const handleClear = () => {
    setSelectedGuest(null);
    setQuery('');
    onGuestSelect(null);
    containerRef.current?.querySelector('input')?.focus();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedGuest) {
      setSelectedGuest(null);
      onGuestSelect(null);
    }

    setQuery(e.target.value);
  };

  const highlightText = (text: string, highlightQuery: string) => {
    if (!highlightQuery.trim()) {
      return <span>{text}</span>;
    }

    const escapedQuery = highlightQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);

    return (
      <span>
        {parts.map((part, index) => {
          const isMatch = part.toLowerCase() === highlightQuery.toLowerCase();
          return isMatch ? (
            <mark key={index} className="search-highlight">
              {part}
            </mark>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </span>
    );
  };

  return (
    <div className="search-container" ref={containerRef}>
      <div className="search-input-shell">
        <Search size={18} className="search-input-shell__icon" />
        <input
          id="search"
          type="text"
          className="input search-input"
          placeholder="Type a guest name..."
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            if (results.length > 0) {
              setShowResults(true);
            }
          }}
          disabled={!!selectedGuest}
        />

        {(isLoading || propIsLoading) && <div className="spinner spinner--small" />}

        {selectedGuest && (
          <button onClick={handleClear} className="clear-button" type="button" aria-label="Clear selected guest">
            <X size={14} />
          </button>
        )}
      </div>

      {showResults && (
        <div className="search-results">
          {results.length > 0 ? (
            results.map((result) => (
              <button
                key={result.guest.id}
                className="search-result-item"
                type="button"
                onClick={() => handleGuestSelect(result.guest)}
              >
                <span className="guest-name">
                  {highlightText(result.guest.name, query)}
                </span>
                <span className="table-info">Table {result.guest.table}</span>
              </button>
            ))
          ) : (
            <div className="search-result-item search-result-item--empty">No guests found.</div>
          )}
        </div>
      )}
    </div>
  );
}
